import { createContext, useContext } from 'react';

/**
 * NavSendContext — canonical path for UI components to dispatch XState
 * machine events.
 *
 * Plan 05-01 Task 1 contract. Replaces the Phase 3 `window.__navSend`
 * debug-escape-hatch in production UI — the debug global remains available
 * under `?debug=1` for console smoke tests, but BoardingOverlay /
 * ReturnToStreetcarButton / TourMenu / StreetcarRig (END_OF_LINE) / etc.
 * all route through this context.
 *
 * The send function signature matches `@xstate/react`'s useMachine send:
 *   send({ type: 'START' }) — discriminated-union machine events.
 *
 * We type this as `unknown`-parameter so every consumer can narrow to its own
 * event payload without importing the whole navMachine event union here
 * (avoids circular imports between NavController → NavContext → UI).
 */
export type NavSend = (event: { type: string; [key: string]: unknown }) => void;

export const NavSendContext = createContext<NavSend | null>(null);

/**
 * useNavSend — hook returning the active send function.
 *
 * Throws if used outside a <NavSendContext.Provider>. This matches the
 * React hook convention (React Query's useQueryClient, React Router's
 * useNavigate, etc.) — render-time errors are loud + early, not silent.
 */
export function useNavSend(): NavSend {
  const send = useContext(NavSendContext);
  if (send === null) {
    throw new Error(
      'useNavSend must be used within <NavSendContext.Provider>. ' +
        'Ensure NavController wraps its children.',
    );
  }
  return send;
}
